import { ComponentInteractionType, SceneComponent } from '@/_models/virtualStaging/Scene.component';
import { LoadingState } from '@/_models/virtualStaging/VSObject';
import { getXZCoordinatesOnCircle } from '@/components/util/math/getXZCoordinatesOnCircle';
import { VRImagePlaneInputs, VRImagePlaneOutputs } from './vrImagePlaneTypes';

export class VRImagePlaneComponent extends SceneComponent {
  public root: any = null;
  public pivot: any = null;
  public imagePlane: any = null;
  public isDestroyed = false;
  private hostPose: any = null;

  inputs: VRImagePlaneInputs = {
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    visible: false,
    imageUrl: '',
    scale: 1,
    planeWidth: 2,
    planeHeight: 1.5,
  };

  outputs: VRImagePlaneOutputs = {
    loadingState: LoadingState.Idle,
  };

  events = {
    [ComponentInteractionType.CLICK]: false,
    [ComponentInteractionType.HOVER]: false,
    [ComponentInteractionType.DRAG]: false,
  };

  declare context: any;

  onInit() {
    this.initRoot();
    this.createImagePlane();
    this.outputs.loadingState = LoadingState.Loaded;
  }

  private initRoot() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    this.root.visible = this.inputs.visible;
    this.root.scale.set(this.inputs.scale, this.inputs.scale, this.inputs.scale);

    // Create pivot for proper rotation
    this.pivot = new THREE.Object3D();
    this.root.add(this.pivot);

    this.outputs.objectRoot = this.root;
    this.outputs.collider = this.root;
    this.outputs.loadingState = LoadingState.Loading;
  }

  private createImagePlane() {
    const THREE = this.context.three;
    const textureLoader = new THREE.TextureLoader();

    textureLoader.load(
      this.inputs.imageUrl,
      (texture) => {
        const material = new THREE.MeshBasicMaterial({
          map: texture,
          transparent: true,
          side: THREE.DoubleSide,
          depthTest: false,
          depthWrite: false,
        });

        // Set render order to ensure it's rendered on top
        material.renderOrder = 999;

        const geometry = new THREE.PlaneGeometry(this.inputs.planeWidth, this.inputs.planeHeight);
        this.imagePlane = new THREE.Mesh(geometry, material);

        // Add to pivot instead of root for proper rotation
        this.pivot.add(this.imagePlane);

        this.outputs.loadingState = LoadingState.Loaded;
      },
      undefined,
      (error) => {
        console.error('Error loading VR image:', error);
        this.outputs.loadingState = LoadingState.Error;
      },
    );
  }

  setHostPose(pose: any) {
    this.hostPose = pose;
  }

  setPositionFromPose(pose: any) {
    if (!pose.rotation || !pose.position) {
      console.error('No pose', pose);
      return;
    }

    // Position the image plane in front of the camera
    // Using a distance of 1.5 meters in front
    const { x, z } = getXZCoordinatesOnCircle(2.2, pose.rotation.y + 180);

    this.root.position.set(
      pose.position.x + x,
      pose.position.y, // Same height as camera
      pose.position.z + z,
    );

    // Make the plane look at the camera position (billboard effect)
    // This ensures it always faces the user without tilting
    this.root.lookAt(pose.position.x, pose.position.y - 0.1, pose.position.z);
  }

  setFromHostPose() {
    if (this.hostPose) {
      this.setPositionFromPose(this.hostPose);
    }
  }

  setVisible(visible: boolean) {
    this.inputs.visible = visible;
    if (this.root) {
      this.root.visible = visible;
    }
  }

  setImageUrl(imageUrl: string) {
    if (this.inputs.imageUrl === imageUrl) return;

    this.inputs.imageUrl = imageUrl;

    // Reload the texture
    if (this.imagePlane) {
      const THREE = this.context.three;
      const textureLoader = new THREE.TextureLoader();

      textureLoader.load(
        imageUrl,
        (texture) => {
          if (this.imagePlane && this.imagePlane.material) {
            // Dispose old texture
            if (this.imagePlane.material.map) {
              this.imagePlane.material.map.dispose();
            }
            this.imagePlane.material.map = texture;
            this.imagePlane.material.needsUpdate = true;
          }
        },
        undefined,
        (error) => {
          console.error('Error reloading VR image:', error);
        },
      );
    }
  }

  onInputsUpdated(previousInputs: this['inputs']) {
    if (!this.root) return;
    this.root.visible = this.inputs.visible;
  }

  // Called every frame when in VR
  cycleVR(_session: any) {
    if (this.hostPose && this.inputs.visible) {
      this.setFromHostPose();
    }
  }

  onTick() {
    if (this.context?.renderer?.xr?.isPresenting) {
      const _session = this.context.renderer.xr.getSession();
      _session.requestAnimationFrame(() => this.cycleVR(_session));
    }
  }

  onDestroy() {
    if (this.imagePlane) {
      if (this.imagePlane.material) {
        if (this.imagePlane.material.map) {
          this.imagePlane.material.map.dispose();
        }
        this.imagePlane.material.dispose();
      }
      if (this.imagePlane.geometry) {
        this.imagePlane.geometry.dispose();
      }
    }
    this.isDestroyed = true;
  }
}

export const vrImagePlaneComponentType = 'mp.vrImagePlaneComponent';
export const makeVRImagePlaneComponent = (): VRImagePlaneComponent => new VRImagePlaneComponent();
